import React, { useCallback, useState, useEffect, useRef } from "react";
import { Calendar as _Calendar, Text, SkeletonLoader } from "@bookingcom/bui-react";
import useCalendar from "./useCalendar.js";
import { isDateInRange, formatDateKey, getPriceVariant } from "../../utils/fareCalendar.js";
import { useFareCalendar, useFareCalendarEnabled } from "../FareCalendar/useFareCalendar.js";
import FareCalendarBadge from "../FareCalendar/FareCalendarBadge.js";
import useScrollBaseMonthChange from "../../hooks/useScrollBaseMonthChange.js";
import { useFareCalendarTracking } from "./useFareCalendarTracking.js";
import styles from "./Calendar.module.css";
import { t } from "@bookingcom/lingojs-core";
const attributes = {
    key: "data-ui-name",
    value: "calendar_body"
};
export default function Calendar(props) {
    const { segmentIndex, onDateChange, onLoadingChange, onErrorChange, onMonthChange, ...restProps } = props;
    const { setDateValues, isRoundTrip, startDate, endDate, fareCalendarConfig, fareCalendarDisclaimerText, i18n, onTrackSBExperimentCustomGoal, onTrackSBExperimentGoalWithValue, ...calendarProps } = useCalendar({
        segmentIndex
    });
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, restProps.mode);
    const fareCalendarDisplayEnabled = fareCalendarEnabled && !!fareCalendarConfig?.isFareCalendarDisplayEnabled;
    const departureDate = useRef(startDate || undefined);
    const { flightsMap, handleMonthChange, loading, error } = useFareCalendar({
        baseDate: departureDate.current,
        mode: restProps.mode,
        enabled: fareCalendarEnabled,
        segmentIndex,
        selectedDepartureDate: startDate || undefined,
        selectedReturnDate: endDate || undefined
    });
    const { trackFareCalendarGoals } = useFareCalendarTracking({
        flightsMap,
        isRoundTrip,
        startDate,
        endDate,
        fareCalendarEnabled,
        onTrackSBExperimentCustomGoal,
        onTrackSBExperimentGoalWithValue
    });
    const handleScrollMonthChange = useCallback((date) => {
        handleMonthChange(date);
    }, [handleMonthChange]);
    useScrollBaseMonthChange({
        isActive: restProps.mode === "vertical" && fareCalendarEnabled,
        selector: attributes,
        monthsCallback: handleScrollMonthChange
    });
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onErrorChange?.(error);
    }, [error, onErrorChange]);
    const [shouldHideAfterReturn, setShouldHideAfterReturn] = useState(false);
    const [shouldHideBeforeDeparture, setShouldHideBeforeDeparture] = useState(false);
    const previousEndDateRef = useRef(null);
    useEffect(() => {
        if (isRoundTrip && endDate && !previousEndDateRef.current) {
            setShouldHideAfterReturn(false);
        }
        else if (previousEndDateRef.current && !endDate) {
            setShouldHideAfterReturn(false);
        }
        previousEndDateRef.current = endDate;
    }, [endDate, isRoundTrip]);
    useEffect(() => {
        if (isRoundTrip && startDate && !endDate) {
            setShouldHideBeforeDeparture(true);
        }
        else if (endDate || !startDate) {
            setShouldHideBeforeDeparture(false);
        }
    }, [startDate, endDate, isRoundTrip]);
    useEffect(() => {
        setShouldHideAfterReturn(false);
        setShouldHideBeforeDeparture(false);
    }, []);
    const handleOnDateChange = useCallback((data) => {
        const { endDate, startDate } = data;
        let offerAccuracyTrackerId;
        if (startDate && flightsMap) {
            const dateKey = formatDateKey(startDate);
            const flight = flightsMap[dateKey];
            offerAccuracyTrackerId = flight?.pricePerAdult?.offerAccuracyTrackerId ?? "";
        }
        trackFareCalendarGoals(startDate, endDate);
        setDateValues({ from: startDate || undefined, to: endDate || undefined });
        onDateChange({
            from: startDate || undefined,
            to: endDate || undefined,
            offerAccuracyTrackerId
        });
    }, [setDateValues, onDateChange, flightsMap, trackFareCalendarGoals]);
    const renderDay = useCallback((date) => {
        const yyyy = date.getFullYear();
        const m = date.getMonth() + 1;
        const d = date.getDate();
        const mm = `0${m}`.slice(-2);
        const dd = `0${d}`.slice(-2);
        return React.createElement("span", { "data-ui-name": `calendar_cell_${yyyy}-${mm}-${dd}` }, d);
    }, []);
    const renderAttachment = useCallback(({ date }) => {
        const dateKey = formatDateKey(date);
        if (!isDateInRange(date, calendarProps.minDate, calendarProps.maxDate)) {
            return undefined;
        }
        if (isRoundTrip) {
            if (shouldHideBeforeDeparture && startDate && date < startDate) {
                return undefined;
            }
            if (shouldHideAfterReturn && endDate && date > endDate) {
                return undefined;
            }
        }
        if (loading) {
            return {
                text: React.createElement(SkeletonLoader, { variant: "one-line", width: "32px", className: styles.fareCalendarSkeleton }),
                ariaLabel: i18n.trans(t("a11y_flights_fare_cal_loading"))
            };
        }
        const flight = flightsMap[dateKey];
        if (!flight || !flight.formattedPrice) {
            return {
                text: "-"
            };
        }
        const variant = getPriceVariant(flight.priceIndication);
        const priceIndicationLabelMap = {
            low: i18n.trans(t("a11y_flights_calendar_indicator_low")),
            medium: i18n.trans(t("a11y_flights_calendar_indicator_average")),
            high: i18n.trans(t("a11y_flights_calendar_indicator_above_average"))
        };
        const ariaLabel = priceIndicationLabelMap[flight.priceIndication] || i18n.trans(t("a11y_flights_calendar_indicator_average"));
        return {
            text: flight.formattedPrice,
            variant,
            ariaLabel
        };
    }, [
        calendarProps.minDate,
        calendarProps.maxDate,
        flightsMap,
        loading,
        isRoundTrip,
        startDate,
        endDate,
        shouldHideAfterReturn,
        shouldHideBeforeDeparture,
        i18n
    ]);
    const onBaseMonthChange = useCallback((date) => {
        onTrackSBExperimentCustomGoal({ name: "flights_web_cat_fare_calendar_oneway", customGoal: 3 });
        handleMonthChange(date);
        onMonthChange?.(date);
    }, [handleMonthChange, onMonthChange]);
    const currencyCode = fareCalendarConfig?.currencyCode;
    const calendarClassName = [
        restProps.mode === "vertical" ? styles.verticalMode : undefined,
        fareCalendarConfig?.isCalendarSpacingsEnabled && fareCalendarDisplayEnabled && restProps.mode !== "vertical"
            ? styles.calendarSpacings
            : undefined
    ]
        .filter(Boolean)
        .join(" ") || undefined;
    return (React.createElement(React.Fragment, null,
        restProps.mode !== "vertical" && currencyCode && fareCalendarDisplayEnabled ? (React.createElement(FareCalendarBadge, { currencyCode: currencyCode, className: styles.fareCalendarBadge })) : undefined,
        React.createElement(_Calendar, { ...calendarProps, ...restProps, onDateChange: handleOnDateChange, startDate: startDate, endDate: isRoundTrip ? endDate : null, baseDate: departureDate.current, renderDay: renderDay, attributes: { [attributes.key]: attributes.value }, renderAttachment: fareCalendarDisplayEnabled ? renderAttachment : undefined, onBaseMonthChange: onBaseMonthChange, className: calendarClassName, previousAriaLabel: i18n.trans(t("a11y_flights_calendar_previous")), nextAriaLabel: i18n.trans(t("a11y_flights_calendar_next")), startAccessibilityHint: i18n.trans(t("a11y_flights_search_date_select_departure")), endAccessibilityHint: isRoundTrip ? i18n.trans(t("a11y_flights_search_date_select_return")) : undefined }),
        fareCalendarDisplayEnabled ? (React.createElement(Text, { variant: "body_2", className: styles.fareCalendarFooter }, fareCalendarDisclaimerText)) : undefined));
}
